// src/pages/ROICalculator/CalculatorForm.jsx

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Button,
  Grid,
  Tooltip,
  IconButton,
  Chip
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

// Constants
import { 
  INDUSTRY_DATA, 
  SLIDER_CONFIG,
  getIndustryLabel,
  getIndustryTypicalMessages,
  getIndustrySocialPosts // EXISTING
} from './constants';

// Calculation utilities
import { calculateROI } from './calculationUtils';

const CalculatorForm = ({ onCalculate }) => {
  const [formData, setFormData] = useState({
    industry: 'restaurant',
    revenue: SLIDER_CONFIG.revenue.default,
    messages: SLIDER_CONFIG.messages.default,
    reviews: SLIDER_CONFIG.reviews.default,
    rating: SLIDER_CONFIG.rating.default,
    socialPosts: SLIDER_CONFIG.socialPosts.default // NEW
  });

  const handleIndustryChange = (event) => {
    const newIndustry = event.target.value;
    const typicalMessages = getIndustryTypicalMessages(newIndustry);
    const typicalSocialPosts = getIndustrySocialPosts(newIndustry); // NEW
    
    setFormData({
      ...formData,
      industry: newIndustry,
      messages: typicalMessages,
      socialPosts: typicalSocialPosts // NEW - Auto-adjust based on industry
    });
  };

  const handleSliderChange = (field) => (event, newValue) => {
    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  const handleCalculate = () => {
    const results = calculateROI(formData);
    onCalculate(results);
  };

  // NEW: Get posting frequency description
  const getPostingFrequencyLabel = (postsPerWeek) => {
    if (postsPerWeek === 0) return 'Not currently posting';
    if (postsPerWeek <= 2) return 'Occasional posting';
    if (postsPerWeek <= 4) return 'Regular posting';
    if (postsPerWeek <= 7) return 'Daily posting';
    if (postsPerWeek <= 14) return 'Very active (2x daily)';
    return 'Highly active (3x daily)';
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: { xs: 3, md: 5 }, 
        borderRadius: 3,
        bgcolor: 'white'
      }}
    >
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 1, 
          fontWeight: 600,
          textAlign: 'center'
        }}
      >
        Tell Us About Your Business
      </Typography>
      <Typography 
        variant="body1" 
        sx={{ 
          mb: 4, 
          color: 'text.secondary',
          textAlign: 'center'
        }}
      >
        Adjust the sliders to match your business profile
      </Typography>

      <Grid container spacing={4}>
        {/* Industry Selection */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="industry-label">Industry</InputLabel>
            <Select
              labelId="industry-label"
              id="industry-select"
              value={formData.industry}
              label="Industry"
              onChange={handleIndustryChange}
              sx={{
                bgcolor: '#f5f7fa',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main'
                }
              }}
            >
              {Object.entries(INDUSTRY_DATA).map(([key, data]) => (
                <MenuItem key={key} value={key}>
                  {data.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Monthly Revenue Slider */}
        <Grid item xs={12}>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {SLIDER_CONFIG.revenue.label}
              </Typography>
              <Tooltip title="Your approximate monthly revenue. This helps estimate the impact of rating improvements on your business.">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {SLIDER_CONFIG.revenue.format(formData.revenue)}
            </Typography>
          </Box>
          <Slider
            value={formData.revenue}
            onChange={handleSliderChange('revenue')}
            min={SLIDER_CONFIG.revenue.min}
            max={SLIDER_CONFIG.revenue.max}
            step={SLIDER_CONFIG.revenue.step}
            valueLabelDisplay="auto"
            valueLabelFormat={SLIDER_CONFIG.revenue.format}
            sx={{
              color: 'primary.main',
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20
              },
              '& .MuiSlider-track': {
                height: 6
              },
              '& .MuiSlider-rail': {
                height: 6,
                bgcolor: '#e0e0e0'
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              $5K
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              $500K
            </Typography>
          </Box>
        </Grid>

        {/* Customer Messages Slider */}
        <Grid item xs={12}>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {SLIDER_CONFIG.messages.label}
              </Typography>
              <Tooltip title="Average number of customer inquiries you receive daily via email, text, social media, or phone. Includes questions, bookings, and support requests.">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {SLIDER_CONFIG.messages.format(formData.messages)}
            </Typography>
          </Box>
          <Slider
            value={formData.messages}
            onChange={handleSliderChange('messages')}
            min={SLIDER_CONFIG.messages.min}
            max={SLIDER_CONFIG.messages.max}
            step={SLIDER_CONFIG.messages.step}
            valueLabelDisplay="auto"
            sx={{
              color: 'primary.main',
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20
              },
              '& .MuiSlider-track': {
                height: 6
              },
              '& .MuiSlider-rail': {
                height: 6,
                bgcolor: '#e0e0e0'
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              5
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              200
            </Typography>
          </Box>
        </Grid>

        {/* Current Reviews Slider */}
        <Grid item xs={12}>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {SLIDER_CONFIG.reviews.label}
              </Typography>
              <Tooltip title="Total number of online reviews your business has across all platforms (Google, Yelp, Facebook, etc.). This helps estimate Smart Rating improvements.">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {SLIDER_CONFIG.reviews.format(formData.reviews)}
            </Typography>
          </Box>
          <Slider
            value={formData.reviews}
            onChange={handleSliderChange('reviews')}
            min={SLIDER_CONFIG.reviews.min}
            max={SLIDER_CONFIG.reviews.max}
            step={SLIDER_CONFIG.reviews.step}
            valueLabelDisplay="auto"
            sx={{
              color: 'primary.main',
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20
              },
              '& .MuiSlider-track': {
                height: 6
              },
              '& .MuiSlider-rail': {
                height: 6,
                bgcolor: '#e0e0e0'
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              0
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              500
            </Typography>
          </Box>
        </Grid>

        {/* Current Rating Slider */}
        <Grid item xs={12}>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {SLIDER_CONFIG.rating.label}
              </Typography>
              <Tooltip title="Your average rating across review platforms. Smart Rating typically increases this by filtering out low-credibility reviews.">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {SLIDER_CONFIG.rating.format(formData.rating)}
            </Typography>
          </Box>
          <Slider
            value={formData.rating}
            onChange={handleSliderChange('rating')}
            min={SLIDER_CONFIG.rating.min}
            max={SLIDER_CONFIG.rating.max}
            step={SLIDER_CONFIG.rating.step}
            valueLabelDisplay="auto"
            valueLabelFormat={(val) => val.toFixed(1)}
            sx={{
              color: 'primary.main',
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20
              },
              '& .MuiSlider-track': {
                height: 6
              },
              '& .MuiSlider-rail': {
                height: 6,
                bgcolor: '#e0e0e0'
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              1.0 ⭐
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              5.0 ⭐
            </Typography>
          </Box>
        </Grid>

        {/* NEW: Social Media Posts Slider */}
        <Grid item xs={12}>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {SLIDER_CONFIG.socialPosts.label}
              </Typography>
              <Tooltip title="How many times per week do you want to post on social media? Includes all platforms (Facebook, Instagram, LinkedIn, Twitter). Research shows daily posting doubles engagement.">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
                {SLIDER_CONFIG.socialPosts.format(formData.socialPosts)}
              </Typography>
              <Chip 
                label={getPostingFrequencyLabel(formData.socialPosts)}
                size="small"
                color={formData.socialPosts >= 7 ? 'success' : formData.socialPosts >= 3 ? 'primary' : 'default'}
                sx={{ fontWeight: 500 }}
              />
            </Box>
          </Box>
          <Slider
            value={formData.socialPosts}
            onChange={handleSliderChange('socialPosts')}
            min={SLIDER_CONFIG.socialPosts.min}
            max={SLIDER_CONFIG.socialPosts.max}
            step={SLIDER_CONFIG.socialPosts.step}
            marks={SLIDER_CONFIG.socialPosts.marks}
            valueLabelDisplay="auto"
            sx={{
              color: 'primary.main',
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20
              },
              '& .MuiSlider-track': {
                height: 6
              },
              '& .MuiSlider-rail': {
                height: 6,
                bgcolor: '#e0e0e0'
              },
              '& .MuiSlider-mark': {
                bgcolor: 'primary.main',
                height: 8,
                width: 2
              },
              '& .MuiSlider-markLabel': {
                fontSize: '0.75rem',
                color: 'text.secondary'
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              None
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              3x Daily
            </Typography>
          </Box>
          
          {/* Social Media Posting Tip */}
          <Box 
            sx={{ 
              mt: 2, 
              p: 1.5, 
              bgcolor: formData.socialPosts >= 5 ? '#e8f5e9' : '#fff3cd',
              borderRadius: 1,
              border: '1px solid',
              borderColor: formData.socialPosts >= 5 ? '#4caf50' : '#ffc107'
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {formData.socialPosts === 0 && (
                <>
                  <strong>💡 Tip:</strong> Not posting on social media? You're missing out on 90% of consumers 
                  who buy from brands they follow. Start with 3-5 posts per week.
                </>
              )}
              {formData.socialPosts >= 1 && formData.socialPosts < 5 && (
                <>
                  <strong>💡 Tip:</strong> Good start! Research shows businesses posting 5-7 times per week 
                  (daily) see 2x more engagement and 20-40% more revenue.
                </>
              )}
              {formData.socialPosts >= 5 && formData.socialPosts <= 7 && (
                <>
                  <strong>✅ Excellent!</strong> Daily posting is the sweet spot for most small businesses. 
                  This frequency maximizes engagement without overwhelming your audience.
                </>
              )}
              {formData.socialPosts > 7 && (
                <>
                  <strong>🚀 High frequency!</strong> Posting multiple times daily works great for visual 
                  businesses (restaurants, retail). Ensure content quality remains high.
                </>
              )}
            </Typography>
          </Box>
        </Grid>

        {/* Calculate Button */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleCalculate}
            sx={{
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s'
            }}
          >
            Calculate My ROI →
          </Button>
        </Grid>
      </Grid>

      {/* Industry Info - UPDATED to include social media */}
      <Box 
        sx={{ 
          mt: 3, 
          p: 2, 
          bgcolor: '#f5f7fa', 
          borderRadius: 2,
          border: '1px solid #e0e0e0'
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          <strong>Industry Average:</strong> {getIndustryLabel(formData.industry)} businesses 
          typically handle {getIndustryTypicalMessages(formData.industry)} customer messages per day,
          post {getIndustrySocialPosts(formData.industry)} times per week on social media,
          with an average transaction value of ${INDUSTRY_DATA[formData.industry]?.avgTransaction}.
        </Typography>
      </Box>
    </Paper>
  );
};

export default CalculatorForm;